import React from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  TablePagination,
  InputAdornment,
  TextField,
  alpha,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FileClock, Search, Download, Users, User, GitCompare, ArrowLeft } from 'lucide-react';
import { BglPrimarySmallButton, BglPrimaryOutlinedSmallButton } from 'components/button/BglButtons';
import { SearchableMultiSelect } from 'components/common/SearchableMultiSelect';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { createAuditApiClient } from 'framework/http/AuditClient';
import type { AuditEvent, GetAuditEventsParams, AuditEventTypeConfig } from 'types/apis/audit';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { exportToCsv } from 'utils/csv-export';

type ActivityMode = 'user' | 'project';
type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
const VALID_CHIP_COLORS = new Set<string>(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']);

const PER_PAGE = 50;

// Administration-level audit view: config/string registry actions are recorded
// under this fixed project_id (matches the backend AUDIT_PROJECT_GLOBAL sentinel),
// not a pilot id.
const ADMIN_PROJECT_ID = 'GLOBAL';
// Registry source names (mirror backend AuditSourceName) — the admin view defaults
// its source filter to these two.
const REGISTRY_AUDIT_SOURCES = ['cms:config-registry', 'cms:string-registry'];

type AuditTrailVariant = 'project' | 'admin';
interface AuditTrailPageProps {
  variant?: AuditTrailVariant;
}

function toDateString(d: Date): string {
  return d.toISOString().split('T')[0];
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from, to };
}

function truncate(val: string | null, max = 80): string {
  if (!val) return '—';
  return val.length > max ? val.slice(0, max) + '…' : val;
}

function toDisplayLabel(val: unknown): string {
  if (typeof val !== 'string') return String(val ?? '');
  return val.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function tryParseJson(val: string | null): Record<string, unknown> | null {
  if (!val) return null;
  try {
    let parsed: unknown = JSON.parse(val);
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch {}
    }
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {}
  return null;
}

function getJsonDiffKeys(oldObj: Record<string, unknown>, newObj: Record<string, unknown>): string[] {
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  return [...allKeys].filter(k => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]));
}

type DiffItem = { path: string; oldVal: unknown; newVal: unknown };

function getDeepDiffItems(oldVal: unknown, newVal: unknown, path = ''): DiffItem[] {
  if (JSON.stringify(oldVal) === JSON.stringify(newVal)) return [];
  const isPlainObj = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);
  if (isPlainObj(oldVal) && isPlainObj(newVal)) {
    const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
    const result: DiffItem[] = [];
    for (const key of allKeys) {
      const childPath = path ? `${path}.${key}` : key;
      result.push(...getDeepDiffItems(oldVal[key], newVal[key], childPath));
    }
    return result;
  }
  return [{ path: path || 'value', oldVal, newVal }];
}

function tryFormatAsJson(val: string | null): string | null {
  if (!val) return null;
  try {
    let parsed: unknown = JSON.parse(val);
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch {}
    }
    if (typeof parsed !== 'undefined') return JSON.stringify(parsed, null, 2);
  } catch {}
  return null;
}

type DiffLine = { type: 'same' | 'removed' | 'added'; line: string };

function computeLineDiff(oldLines: string[], newLines: string[]): DiffLine[] {
  const m = oldLines.length, n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldLines[i - 1] === newLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const result: DiffLine[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({ type: 'same', line: oldLines[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'added', line: newLines[j - 1] });
      j--;
    } else {
      result.unshift({ type: 'removed', line: oldLines[i - 1] });
      i--;
    }
  }
  return result;
}

function renderDiffJson(
  diffLines: DiffLine[],
  side: 'old' | 'new',
  highlightColor: 'error' | 'success'
): React.ReactNode {
  const targetType = side === 'old' ? 'removed' : 'added';
  return diffLines
    .filter(d => d.type === 'same' || d.type === targetType)
    .map((d, i) => (
      <Box
        key={i}
        component="span"
        sx={{
          display: 'block',
          bgcolor: d.type === targetType
            ? (theme: any) => alpha(theme.palette[highlightColor].main, 0.25)
            : 'transparent',
          mx: -1.5,
          px: 1.5,
        }}
      >
        {d.line}
      </Box>
    ));
}

function exportCsv(events: AuditEvent[]) {
  const headers = ['User', 'Timestamp', 'Action', 'Source', 'Entity Key', 'Old Value', 'New Value'];
  const rows = events.map(e => [
    e.user_email,
    e.created_at,
    e.event_type,
    e.source_name,
    e.entity_key,
    e.old_value,
    e.new_value,
  ]);
  exportToCsv(headers, rows, `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
}

export const AuditTrailPage: React.FC<AuditTrailPageProps> = ({ variant = 'project' }) => {
  const isAdmin = variant === 'admin';
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduxProjectId = useSelector(selectSelectedPilotId);

  const urlProjectId = searchParams.get('projectId') || '';
  const rawSource = searchParams.get('source');

  const SOURCE_DISPLAY_NAMES: Record<string, string> = {
    cx_editor: 'CX Editor',
    cms: 'CMS',
  };
  const backLabel = rawSource
    ? `Back to ${SOURCE_DISPLAY_NAMES[rawSource] ?? toDisplayLabel(rawSource)}`
    : 'Back';

  // Admin view is fixed to the GLOBAL project; project view resolves the pilot
  // from the URL / redux / manual input.
  const initialProjectId = isAdmin ? ADMIN_PROJECT_ID : (urlProjectId || reduxProjectId || '');

  const [projectIdInput, setProjectIdInput] = React.useState(initialProjectId);
  const [pilotId, setPilotId] = React.useState(initialProjectId);

  const [activityMode, setActivityMode] = React.useState<ActivityMode>(isAdmin ? 'project' : 'user');
  const [events, setEvents] = React.useState<AuditEvent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [eventTypeFilter, setEventTypeFilter] = React.useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = React.useState<string[]>(
    isAdmin ? REGISTRY_AUDIT_SOURCES : (rawSource ? [rawSource] : [])
  );
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [dateRange, setDateRange] = React.useState<{ from: Date; to: Date }>(defaultDateRange);
  const [availableEventTypes, setAvailableEventTypes] = React.useState<string[]>([]);
  const [availableSources, setAvailableSources] = React.useState<string[]>([]);
  const [compareEvent, setCompareEvent] = React.useState<AuditEvent | null>(null);
  const [eventTypeConfigs, setEventTypeConfigs] = React.useState<Record<string, AuditEventTypeConfig>>({});

  React.useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.ADMIN.AUDIT_LOG_OPENED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!pilotId) return;
    (async () => {
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const sources = await api.getAuditSources({ project_id: pilotId });
        setAvailableSources(sources);
      } catch {}
    })();
  }, [pilotId, rc]);

  React.useEffect(() => {
    if (!pilotId) return;
    (async () => {
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const params: { project_id: string; source_names?: string } = { project_id: pilotId };
        if (sourceFilter.length > 0) params.source_names = sourceFilter.join(',');
        const configs: AuditEventTypeConfig[] = await api.getAuditEventTypes(params);
        setAvailableEventTypes(configs.map(c => c.event_type));
        const map: Record<string, AuditEventTypeConfig> = {};
        for (const c of configs) map[c.event_type] = c;
        setEventTypeConfigs(map);
        setEventTypeFilter([]);
      } catch {}
    })();
  }, [pilotId, rc, sourceFilter]);

  React.useEffect(() => {
    if (!pilotId) return;
    let ignore = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const api = createAuditApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const params: GetAuditEventsParams = {
          project_id: pilotId,
          page: page + 1,
          per_page: PER_PAGE,
          from_date: toDateString(dateRange.from),
          to_date: toDateString(dateRange.to),
        };
        if (sourceFilter.length > 0) params.source_names = sourceFilter.join(',');
        if (eventTypeFilter.length > 0) params.event_type = eventTypeFilter.join(',');
        const data = activityMode === 'user'
          ? await api.getUserAuditEvents(params)
          : await api.getProjectAuditEvents(params);
        if (!ignore) {
          setEvents(data.data || []);
          setTotal(data.total || 0);
        }
      } catch {
        if (!ignore) setError('Failed to load audit events');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, [pilotId, page, eventTypeFilter, sourceFilter, dateRange, rc, activityMode]);

  React.useEffect(() => {
    setPage(0);
  }, [eventTypeFilter, sourceFilter, dateRange, activityMode, pilotId]);

  const filteredEvents = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? events.filter(e =>
          (e.user_email || '').toLowerCase().includes(term) ||
          (e.event_type || '').toLowerCase().includes(term) ||
          (e.entity_key || '').toLowerCase().includes(term) ||
          (e.old_value || '').toLowerCase().includes(term) ||
          (e.new_value || '').toLowerCase().includes(term)
        )
      : events;
    return [...filtered].sort((a, b) => {
      const cmp = a.created_at.localeCompare(b.created_at);
      return sortDirection === 'desc' ? -cmp : cmp;
    });
  }, [events, searchTerm, sortDirection]);

  const sourceDisplayOptions = availableSources.map(toDisplayLabel);
  const eventTypeDisplayOptions = availableEventTypes.map(toDisplayLabel);
  const sourceRawByDisplay = Object.fromEntries(availableSources.map(s => [toDisplayLabel(s), s]));
  const eventTypeRawByDisplay = Object.fromEntries(availableEventTypes.map(t => [toDisplayLabel(t), t]));
  const sourceFilterDisplay = sourceFilter.map(toDisplayLabel);
  const eventTypeFilterDisplay = eventTypeFilter.map(toDisplayLabel);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
          <Toolbar sx={{ gap: 1.5 }}>
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 500 }}
            >
              {backLabel}
            </Button>

            {isAdmin ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                <FileClock size={18} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Registry audit trail
                </Typography>
                <Chip label={ADMIN_PROJECT_ID} size="small" color="primary" sx={{ fontWeight: 600 }} />
              </Box>
            ) : (
              <>
                <TextField
                  size="small"
                  label="Project ID"
                  value={projectIdInput}
                  onChange={e => setProjectIdInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') setPilotId(projectIdInput.trim()); }}
                  sx={{ minWidth: 150, ml: 1 }}
                  placeholder="Enter project ID…"
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSourceFilter([]);
                    setPilotId(projectIdInput.trim());
                  }}
                  disabled={!projectIdInput.trim() || projectIdInput.trim() === pilotId}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Load
                </Button>
              </>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <BglPrimaryOutlinedSmallButton
                startIcon={<Download size={16} />}
                onClick={() => exportCsv(filteredEvents)}
                disabled={filteredEvents.length === 0}
              >
                Export CSV
              </BglPrimaryOutlinedSmallButton>
              {activityMode === 'user' ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Users size={14} />}
                  onClick={() => setActivityMode('project')}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  View Project Activity
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<User size={14} />}
                  onClick={() => setActivityMode('user')}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                >
                  View My Activity
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: 3 }}>

          {/* Filters row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search user, action, entity…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />

            <SearchableMultiSelect
              label="Sources"
              options={sourceDisplayOptions}
              selected={sourceFilterDisplay}
              onChange={labels => {
                rc.analytics.trackEvent(ANALYTICS_EVENTS.ADMIN.AUDIT_LOG_FILTERED, { filter_type: 'source' });
                setSourceFilter(labels.map(l => sourceRawByDisplay[l] ?? l));
              }}
              labelPosition="inline"
            />

            <SearchableMultiSelect
              label="Actions"
              options={eventTypeDisplayOptions}
              selected={eventTypeFilterDisplay}
              onChange={labels => {
                rc.analytics.trackEvent(ANALYTICS_EVENTS.ADMIN.AUDIT_LOG_FILTERED, { filter_type: 'action' });
                setEventTypeFilter(labels.map(l => eventTypeRawByDisplay[l] ?? l));
              }}
              labelPosition="inline"
            />

            <DatePicker
              label="From"
              value={dateRange.from}
              onChange={date => date && setDateRange(d => ({ ...d, from: date }))}
              maxDate={dateRange.to}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="To"
              value={dateRange.to}
              onChange={date => date && setDateRange(d => ({ ...d, to: date }))}
              minDate={dateRange.from}
              maxDate={new Date()}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>

          {!pilotId && (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              <FileClock size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary">Enter a Project ID to load audit events</Typography>
            </Box>
          )}

          {pilotId && (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {error && (
                  <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
                )}
                {!loading && !error && (
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '12%', minWidth: 140 }}>User</TableCell>
                        <TableCell sx={{ width: '11%', minWidth: 130 }}>
                          <TableSortLabel
                            active
                            direction={sortDirection}
                            onClick={() => setSortDirection(d => (d === 'desc' ? 'asc' : 'desc'))}
                          >
                            Timestamp
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: '8%', minWidth: 90 }}>Source</TableCell>
                        <TableCell sx={{ width: '9%', minWidth: 100 }}>Action</TableCell>
                        <TableCell sx={{ width: '12%', minWidth: 140 }}>Entity</TableCell>
                        <TableCell sx={{ width: '48%', minWidth: 360 }}>Changes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEvents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No events found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEvents.map(event => (
                          <TableRow key={event.id} hover>
                            <TableCell>{event.user_email || '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {new Date(event.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                {event.source_name || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const cfg = eventTypeConfigs[event.event_type];
                                const hasHex = !!cfg?.hex;
                                const chipColor = !hasHex && cfg?.color && VALID_CHIP_COLORS.has(cfg.color)
                                  ? (cfg.color as ChipColor)
                                  : 'default';
                                return (
                                  <Chip
                                    label={toDisplayLabel(event.event_type)}
                                    color={hasHex ? undefined : chipColor}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', ...(hasHex && { bgcolor: cfg!.hex, color: '#fff' }) }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {truncate(event.entity_key, 60)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const oldObj = tryParseJson(event.old_value);
                                const newObj = tryParseJson(event.new_value);
                                const isJson = !!(oldObj || newObj);
                                const diffItems: DiffItem[] = oldObj && newObj
                                  ? getDeepDiffItems(oldObj, newObj)
                                  : oldObj
                                  ? Object.keys(oldObj).map(k => ({ path: k, oldVal: (oldObj as Record<string, unknown>)[k], newVal: undefined }))
                                  : newObj
                                  ? Object.keys(newObj).map(k => ({ path: k, oldVal: undefined, newVal: (newObj as Record<string, unknown>)[k] }))
                                  : [];
                                const displayItems = diffItems.slice(0, 4);
                                const moreCount = diffItems.length - displayItems.length;

                                if (isJson) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {diffItems.length === 0 && (
                                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                                        )}
                                        {displayItems.map(item => (
                                          <Box key={item.path} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                            <Tooltip title={item.path} placement="top">
                                              <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary', flexShrink: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.path}:
                                              </Typography>
                                            </Tooltip>
                                            {item.oldVal !== undefined && (
                                              <Tooltip title={JSON.stringify(item.oldVal)} placement="top">
                                                <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: 'error.dark', bgcolor: (theme) => alpha(theme.palette.error.main, 0.12), px: 0.5, borderRadius: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100, flexShrink: 1 }}>
                                                  {truncate(JSON.stringify(item.oldVal), 30)}
                                                </Typography>
                                              </Tooltip>
                                            )}
                                            {item.oldVal !== undefined && item.newVal !== undefined && (
                                              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', flexShrink: 0 }}>→</Typography>
                                            )}
                                            {item.newVal !== undefined && (
                                              <Tooltip title={JSON.stringify(item.newVal)} placement="top">
                                                <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: 'success.dark', bgcolor: (theme) => alpha(theme.palette.success.main, 0.12), px: 0.5, borderRadius: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100, flexShrink: 1 }}>
                                                  {truncate(JSON.stringify(item.newVal), 30)}
                                                </Typography>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        ))}
                                        {moreCount > 0 && (
                                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 }}>
                                            +{moreCount} more
                                          </Typography>
                                        )}
                                      </Box>
                                      <Tooltip title="Compare full JSON">
                                        <IconButton size="small" onClick={() => setCompareEvent(event)} sx={{ flexShrink: 0, mt: 0.25 }}>
                                          <GitCompare size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  );
                                }

                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {event.old_value && (
                                        <Tooltip title={event.old_value.length > 80 ? event.old_value : ''} placement="left">
                                          <Box sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), borderLeft: '3px solid', borderLeftColor: 'error.main', px: 0.75, py: 0.25, mb: event.new_value ? 0.5 : 0, borderRadius: '2px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'error.dark', fontFamily: 'monospace' }}>
                                              {truncate(event.old_value, 80)}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {event.new_value && (
                                        <Tooltip title={event.new_value.length > 80 ? event.new_value : ''} placement="left">
                                          <Box sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), borderLeft: '3px solid', borderLeftColor: 'success.main', px: 0.75, py: 0.25, borderRadius: '2px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'success.dark', fontFamily: 'monospace' }}>
                                              {truncate(event.new_value, 80)}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {!event.old_value && !event.new_value && (
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>—</Typography>
                                      )}
                                    </Box>
                                    {(event.old_value || event.new_value) && (
                                      <Tooltip title="View full changes">
                                        <IconButton size="small" onClick={() => setCompareEvent(event)} sx={{ flexShrink: 0 }}>
                                          <GitCompare size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {!loading && !error && (
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={PER_PAGE}
                  rowsPerPageOptions={[PER_PAGE]}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* JSON Compare Dialog */}
      <Dialog open={!!compareEvent} onClose={() => setCompareEvent(null)} fullWidth maxWidth="lg">
        <AppBar position="static" color="default" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ gap: 1 }} variant="dense">
            <GitCompare size={18} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Change Details</Typography>
            {compareEvent && (
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                {toDisplayLabel(compareEvent.event_type)} by {compareEvent.user_email || '—'} — {new Date(compareEvent.created_at).toLocaleString()}
              </Typography>
            )}
          </Toolbar>
        </AppBar>
        <DialogContent dividers>
          {compareEvent && (() => {
            const oldObj = tryParseJson(compareEvent.old_value);
            const newObj = tryParseJson(compareEvent.new_value);
            const oldFormatted = oldObj ? null : tryFormatAsJson(compareEvent.old_value);
            const newFormatted = newObj ? null : tryFormatAsJson(compareEvent.new_value);
            const isAnyJson = !!(oldObj || newObj || oldFormatted || newFormatted);
            const diffKeys = oldObj && newObj ? getJsonDiffKeys(oldObj, newObj) : [];
            const diffLines = oldObj && newObj
              ? computeLineDiff(
                  JSON.stringify(oldObj, null, 2).split('\n'),
                  JSON.stringify(newObj, null, 2).split('\n')
                )
              : null;

            if (isAnyJson) {
              return (
                <>
                  {diffKeys.length > 0 && (
                    <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontFamily: 'monospace' }}>
                        CHANGED: {diffKeys.join(' · ')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>Before</Typography>
                      <Box component="pre" sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.error.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.72rem', fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {oldObj && diffLines
                          ? renderDiffJson(diffLines, 'old', 'error')
                          : (oldObj ? JSON.stringify(oldObj, null, 2) : (oldFormatted || compareEvent.old_value || '—'))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>After</Typography>
                      <Box component="pre" sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.success.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.72rem', fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {newObj && diffLines
                          ? renderDiffJson(diffLines, 'new', 'success')
                          : (newObj ? JSON.stringify(newObj, null, 2) : (newFormatted || compareEvent.new_value || '—'))}
                      </Box>
                    </Box>
                  </Box>
                </>
              );
            }
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>Before</Typography>
                  <Box sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.error.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {compareEvent.old_value || '—'}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>After</Typography>
                  <Box sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: (theme) => alpha(theme.palette.success.main, 0.2), borderRadius: 1, p: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {compareEvent.new_value || '—'}
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <BglPrimarySmallButton onClick={() => setCompareEvent(null)}>Close</BglPrimarySmallButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AuditTrailPage;
